// Cache singleton de app_settings.
//
// Padrão portado de saude-livre apps/web/src/lib/settings.ts: o shell carrega
// uma vez no init, e as features consomem por getters SÍNCRONOS com fallback
// hardcoded. O ponto do fallback é que a primeira renderização nunca trave
// esperando o banco — e que o sistema continue coerente se a consulta falhar.
//
// Os defaults abaixo espelham os seeds da migration 004. Se mudar lá, mude
// aqui: a pergunta que este arquivo responde é "se o banco não responder, com
// que valor o sistema opera?".

import { getSupabase } from './supabase';

export type SettingValueType =
  | 'number'
  | 'percent'
  | 'days'
  | 'money'
  | 'boolean'
  | 'text'
  | 'json';

export interface AppSetting {
  key: string;
  value: unknown;
  label: string;
  description: string | null;
  category: string;
  value_type: SettingValueType;
  updated_by: string | null;
  updated_at: string;
}

let cache: Map<string, AppSetting> | null = null;
let inflightLoad: Promise<void> | null = null;
let erroCarregamento: unknown = null;

// ── Defaults ─────────────────────────────────────────────────────
const DEFAULT_CAIXA_ABERTURA_PADRAO = 100; // reais
const DEFAULT_TOLERANCIA_DIVERGENCIA_CENTAVOS = 0;
const DEFAULT_SOBRA_ALERTA_TRANSITO_DIAS = 3;
const DEFAULT_PRONTUARIO_ALERTAR_VAGAS = 20;
const DEFAULT_ORCAMENTO_DIAS_PARA_ESFRIAR = 15;
const DEFAULT_CLINICORP_LIMITE_POR_HORA = 20;
const DEFAULT_CLINICORP_RESERVA_INTERATIVA = 6;
const DEFAULT_CLINICORP_RAW_RETENCAO_DIAS = 180;
const DEFAULT_SYNC_BACKFILL_MAX_POR_RUN = 4;
const DEFAULT_EXIGIR_MOTIVO_ABERTURA = true;

// ── Carga ────────────────────────────────────────────────────────

export async function loadSettings(): Promise<void> {
  if (cache) return;
  if (inflightLoad) {
    await inflightLoad;
    return;
  }
  inflightLoad = (async () => {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.from('app_settings').select('*');
      if (error) throw error;
      const map = new Map<string, AppSetting>();
      for (const r of (data ?? []) as AppSetting[]) map.set(r.key, r);
      cache = map;
      erroCarregamento = null;
    } catch (err) {
      console.error('[settings] falha ao carregar; usando defaults', err);
      // Cache vazio, não nulo: evita tentar de novo a cada getter.
      cache = new Map();
      erroCarregamento = err;
    } finally {
      inflightLoad = null;
    }
  })();
  await inflightLoad;
}

/**
 * O último carregamento falhou?
 *
 * Os getters caem no fallback em silêncio de propósito — a primeira render não
 * pode travar esperando o banco. Mas uma tela que EXISTE para mostrar esses
 * valores precisa saber a diferença entre "não há parâmetro" e "não consegui
 * ler". Sem isto, uma falha total de leitura aparece como uma tela vazia
 * educada, que foi exatamente o que aconteceu quando faltavam os GRANTs.
 */
export function getErroCarregamento(): unknown {
  return erroCarregamento;
}

export function invalidateSettingsCache(): void {
  cache = null;
}

/** Todas as settings, ordenadas por categoria e label. A tela de Configurações
 *  se renderiza a partir disto — não há lista de campos hardcoded em lugar
 *  nenhum. Parâmetro novo é um INSERT na 004 e aparece sozinho. */
export function listSettingsFromCache(): AppSetting[] {
  if (!cache) return [];
  return [...cache.values()].sort((a, b) => {
    if (a.category !== b.category) return a.category.localeCompare(b.category, 'pt');
    return a.label.localeCompare(b.label, 'pt');
  });
}

// ── Getters síncronos ────────────────────────────────────────────

function getNumber(key: string, fallback: number): number {
  const v = cache?.get(key)?.value;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return fallback;
}

function getBoolean(key: string, fallback: boolean): boolean {
  const v = cache?.get(key)?.value;
  return typeof v === 'boolean' ? v : fallback;
}

/** Caixa de abertura padrão, em CENTAVOS (o seed está em reais). */
export function getCaixaAberturaPadraoCentavos(): number {
  return Math.round(getNumber('caixa.abertura_padrao', DEFAULT_CAIXA_ABERTURA_PADRAO) * 100);
}

export function getToleranciaDivergenciaCentavos(): number {
  return getNumber('caixa.tolerancia_divergencia_centavos', DEFAULT_TOLERANCIA_DIVERGENCIA_CENTAVOS);
}

export function getSobraAlertaTransitoDias(): number {
  return getNumber('sobra.alerta_transito_dias', DEFAULT_SOBRA_ALERTA_TRANSITO_DIAS);
}

export function getProntuarioAlertarVagasAbaixoDe(): number {
  return getNumber('prontuario.alertar_vagas_abaixo_de', DEFAULT_PRONTUARIO_ALERTAR_VAGAS);
}

export function getOrcamentoDiasParaEsfriar(): number {
  return getNumber('orcamento.dias_para_esfriar', DEFAULT_ORCAMENTO_DIAS_PARA_ESFRIAR);
}

export function getClinicorpLimitePorHora(): number {
  return getNumber('clinicorp.limite_por_hora', DEFAULT_CLINICORP_LIMITE_POR_HORA);
}

export function getClinicorpReservaInterativa(): number {
  return getNumber('clinicorp.reserva_interativa', DEFAULT_CLINICORP_RESERVA_INTERATIVA);
}

export function getClinicorpRawRetencaoDias(): number {
  return getNumber('clinicorp.raw_retencao_dias', DEFAULT_CLINICORP_RAW_RETENCAO_DIAS);
}

export function getSyncBackfillMaxPorRun(): number {
  return getNumber('sync.backfill_max_por_run', DEFAULT_SYNC_BACKFILL_MAX_POR_RUN);
}

export function getExigirMotivoAoSobrescreverAbertura(): boolean {
  return getBoolean('mapa.exigir_motivo_ao_sobrescrever_abertura', DEFAULT_EXIGIR_MOTIVO_ABERTURA);
}

// ── Escrita ──────────────────────────────────────────────────────

/** Requer a permissão configuracoes.editar — quem garante é o RLS. */
export async function updateSetting(key: string, value: unknown): Promise<void> {
  const supabase = getSupabase();
  const { data: userData } = await supabase.auth.getUser();
  const { error } = await supabase
    .from('app_settings')
    .update({ value: value as never, updated_by: userData.user?.id ?? null })
    .eq('key', key);
  if (error) throw error;
  invalidateSettingsCache();
  await loadSettings();
}
